import { twMerge } from 'tailwind-merge';

/**
 * `classes` without the classes `delta` overrides (same group: `text-sm` against `text-lg`) or
 * repeats. Hot edit writes a style change as `stripConflicting(existing, delta) + delta`, so a
 * preset replaces the class of its group instead of piling up, and saving the same change twice
 * leaves one copy.
 */
export function stripConflicting(classes: string, delta: string): string {
  const deltaTokens = new Set(delta.split(/\s+/).filter(Boolean));
  if (deltaTokens.size === 0) return classes.trim();
  return classes
    .split(/\s+/)
    .filter(Boolean)
    .filter((tok) => !deltaTokens.has(tok) && twMerge(`${tok} ${delta}`).split(/\s+/).includes(tok))
    .join(' ');
}

/**
 * `classes` without `remove`, leaving everything else alone. Taking `hidden` back off is how the
 * founder unhides: the display the element had is still in the source, so it comes back as it was.
 */
export function removeClasses(classes: string, remove: string): string {
  const gone = new Set(remove.split(/\s+/).filter(Boolean));
  if (gone.size === 0) return classes.trim();
  return classes
    .split(/\s+/)
    .filter(Boolean)
    .filter((tok) => !gone.has(tok))
    .join(' ');
}

/** `classes` with `delta` applied: conflicting classes removed, `delta` added at the end. */
export function applyClassDelta(classes: string, delta: string): string {
  return [stripConflicting(classes, delta), delta.trim()].filter(Boolean).join(' ');
}

/** Plain utility append (semantic tokens such as bg-primary). */
export function mergeTailwindAppend(existingClassName: string, utilities: string): string {
  if (!utilities.trim()) return twMerge(existingClassName);
  return twMerge(`${existingClassName} ${utilities.trim()}`.trim());
}

/** Merge Tailwind-ish classes after applying a CSS-ish tweak (camelCase keys like computed styles). */
export function mergeTailwindFromCssEdit(
  existingClassName: string,
  cssProperty: string,
  rawValue: string,
): string {
  if (cssProperty === '__hot_tw_util') return mergeTailwindAppend(existingClassName, rawValue);
  const token = cssPropertyToken(cssProperty, rawValue.trim());
  if (!token) return twMerge(existingClassName);
  return twMerge(`${existingClassName} ${token}`.trim());
}

function cssPropertyToken(prop: string, value: string): string | null {
  if (!value) return null;

  switch (prop) {
    case 'fontFamily':
      return arbitrary('font', value.split(',')[0]?.trim()?.replace(/["']/g, '') ?? '');
    case 'fontSize':
      return arbitrary('text', normalizePx(value));
    case 'fontWeight':
      return weightToken(value);
    case 'color':
      return arbitrary('text', normalizeColor(value));
    case 'textAlign':
      return alignToken(value);
    case 'lineHeight':
      return arbitrary('leading', value.replace(/\s+/g, ''));
    case 'letterSpacing':
      return arbitrary('tracking', normalizeSpacing(value));
    case 'width':
      return arbitrary('w', normalizePx(value));
    case 'height':
      return arbitrary('h', normalizePx(value));
    case 'opacity':
      return opacityToken(value);
    case 'paddingTop':
      return arbitrary('pt', normalizePx(value));
    case 'paddingRight':
      return arbitrary('pr', normalizePx(value));
    case 'paddingBottom':
      return arbitrary('pb', normalizePx(value));
    case 'paddingLeft':
      return arbitrary('pl', normalizePx(value));
    case 'marginTop':
      return arbitrary('mt', normalizePx(value));
    case 'marginRight':
      return arbitrary('mr', normalizePx(value));
    case 'marginBottom':
      return arbitrary('mb', normalizePx(value));
    case 'marginLeft':
      return arbitrary('ml', normalizePx(value));
    case 'borderTopWidth':
      return arbitrary('border-t', normalizePx(value));
    case 'borderRightWidth':
      return arbitrary('border-r', normalizePx(value));
    case 'borderBottomWidth':
      return arbitrary('border-b', normalizePx(value));
    case 'borderLeftWidth':
      return arbitrary('border-l', normalizePx(value));
    case 'borderStyle':
      return borderStyleToken(value);
    case 'borderTopColor':
      return arbitrary('border', normalizeColor(value));
    case 'borderRadius':
      return arbitrary('rounded', normalizePx(value));
    case 'gap':
      return arbitrary('gap', normalizePx(value));
    case 'flexDirection':
      return flexDirToken(value);
    case 'justifyContent':
      return justifyToken(value);
    case 'alignItems':
      return itemsToken(value);
    case 'display':
      return displayToken(value);
    case 'backgroundColor':
      return arbitrary('bg', normalizeColorCss(value));
    case 'fill':
      return arbitrary('fill', normalizeColorCss(value));
    case 'stroke':
      return arbitrary('stroke', normalizeColorCss(value));
    case 'objectFit':
      return objectFitToken(value);
    default:
      return null;
  }
}

function arbitrary(prefix: string, raw: string): string {
  const safe = raw.replace(/]/g, '\\]').replace(/\s+/g, '');
  return `${prefix}-[${safe}]`;
}

function normalizePx(value: string): string {
  const v = value.trim();
  if (/^-?[0-9.]+$/.test(v)) return `${v}px`;
  return v;
}

function normalizeSpacing(value: string): string {
  const v = value.trim();
  if (v === 'normal') return 'normal';
  return normalizePx(v);
}

function normalizeColor(value: string): string {
  const v = value.trim();
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(v);
  if (rgb) {
    const r = Number(rgb[1]);
    const g = Number(rgb[2]);
    const b = Number(rgb[3]);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return v;
}

function toHex(n: number): string {
  return Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
}

function alignToken(value: string): string {
  switch (value) {
    case 'start':
    case 'left':
      return 'text-left';
    case 'center':
      return 'text-center';
    case 'end':
    case 'right':
      return 'text-right';
    case 'justify':
      return 'text-justify';
    default:
      return arbitrary('text', value);
  }
}

function weightToken(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return arbitrary('font', value);
  if (n <= 350) return 'font-light';
  if (n <= 450) return 'font-normal';
  if (n <= 550) return 'font-medium';
  if (n <= 650) return 'font-semibold';
  return 'font-bold';
}

function opacityToken(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return arbitrary('opacity', value);
  const unit = n <= 1 ? String(n) : String(n / 100);
  return arbitrary('opacity', unit);
}

function normalizeColorCss(value: string): string {
  const v = value.trim();
  if (/^rgba?\(/i.test(v) || /^hsla?\(/i.test(v)) return stripSpacesColor(v);
  return normalizeColor(v);
}

function stripSpacesColor(v: string): string {
  return v.replace(/\s+/g, '');
}

function flexDirToken(value: string): string {
  switch (value.trim()) {
    case 'row':
      return 'flex-row';
    case 'row-reverse':
      return 'flex-row-reverse';
    case 'column':
    case 'col':
      return 'flex-col';
    case 'column-reverse':
      return 'flex-col-reverse';
    default:
      return arbitrary('flex', value.trim());
  }
}

function justifyToken(value: string): string {
  switch (value.trim()) {
    case 'flex-start':
    case 'start':
      return 'justify-start';
    case 'flex-end':
    case 'end':
      return 'justify-end';
    case 'center':
      return 'justify-center';
    case 'space-between':
      return 'justify-between';
    case 'space-around':
      return 'justify-around';
    case 'space-evenly':
      return 'justify-evenly';
    default:
      return arbitrary('justify', value.trim());
  }
}

function itemsToken(value: string): string {
  switch (value.trim()) {
    case 'flex-start':
    case 'start':
      return 'items-start';
    case 'flex-end':
    case 'end':
      return 'items-end';
    case 'center':
      return 'items-center';
    case 'stretch':
      return 'items-stretch';
    case 'baseline':
      return 'items-baseline';
    default:
      return arbitrary('items', value.trim());
  }
}

function displayToken(value: string): string | null {
  switch (value.trim()) {
    case 'flex':
      return 'flex';
    case 'inline-flex':
      return 'inline-flex';
    case 'grid':
      return 'grid';
    case 'inline-grid':
      return 'inline-grid';
    case 'block':
      return 'block';
    case 'inline-block':
      return 'inline-block';
    case 'inline':
      return 'inline';
    case 'hidden':
      return 'hidden';
    default:
      return null;
  }
}

function objectFitToken(value: string): string {
  switch (value.trim()) {
    case 'cover':
      return 'object-cover';
    case 'contain':
      return 'object-contain';
    case 'fill':
      return 'object-fill';
    case 'none':
      return 'object-none';
    case 'scale-down':
      return 'object-scale-down';
    default:
      return arbitrary('object', value.trim());
  }
}

function borderStyleToken(value: string): string {
  switch (value) {
    case 'solid':
      return 'border-solid';
    case 'dashed':
      return 'border-dashed';
    case 'dotted':
      return 'border-dotted';
    case 'none':
      return 'border-none';
    default:
      return arbitrary('border', value);
  }
}
